import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

interface SwipeState {
  startX: number;
  startY: number;
  startTime: number;
}

interface UseSwipeOptions {
  /** Current screen/category identifier */
  current: string;
  /** List of all screens/categories in order */
  screens: readonly string[];
  /** Base path for navigation (e.g., '/settings' or '/onboarding') */
  basePath: string;
  /** Whether to wrap around at boundaries (default: false) */
  wrapAround?: boolean;
}

/** Minimum horizontal distance (in pixels) required for a valid swipe */
const MIN_SWIPE_DISTANCE = 50;

/** Minimum swipe velocity (pixels per millisecond) required for a valid swipe */
const MIN_SWIPE_VELOCITY = 0.3;

/**
 * Unified swipe navigation hook
 *
 * Handles horizontal swipe gestures for navigation between screens.
 * Used for both settings and onboarding flows.
 *
 * @param options - Configuration options
 * @returns Swipe event handlers and ref for the swipeable container
 *
 * @example
 * ```tsx
 * // Settings (with wrap-around)
 * const swipe = useSwipe({
 *   current: 'assets',
 *   screens: SETTINGS_CATEGORIES,
 *   basePath: '/settings',
 *   wrapAround: true
 * });
 *
 * // Onboarding (no wrap-around)
 * const swipe = useSwipe({
 *   current: 'profile',
 *   screens: ONBOARDING_SCREENS,
 *   basePath: '/onboarding',
 *   wrapAround: false
 * });
 * ```
 */
export function useSwipe({
  current,
  screens,
  basePath,
  wrapAround = false,
}: UseSwipeOptions) {
  const navigate = useNavigate();
  const swipeRef = useRef<HTMLDivElement>(null);
  const [swipeState, setSwipeState] = useState<SwipeState | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setSwipeState({
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
    });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!swipeState) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - swipeState.startX;
    const deltaY = touch.clientY - swipeState.startY;
    const deltaTime = Date.now() - swipeState.startTime;

    // Calculate if this is a horizontal swipe
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    const velocity = absX / deltaTime;

    // Must be more horizontal than vertical, meet minimum distance, and be fast enough
    if (
      absX > absY &&
      absX >= MIN_SWIPE_DISTANCE &&
      velocity > MIN_SWIPE_VELOCITY
    ) {
      const currentIndex = screens.indexOf(current);

      if (deltaX < 0) {
        // Swipe left - go to next screen
        let nextIndex = currentIndex + 1;

        if (wrapAround) {
          // Wrap around to beginning
          nextIndex = nextIndex % screens.length;
        } else if (nextIndex >= screens.length) {
          // Don't navigate if at end
          setSwipeState(null);
          return;
        }

        navigate(`${basePath}/${screens[nextIndex]}`);
      } else {
        // Swipe right - go to previous screen
        let prevIndex = currentIndex - 1;

        if (wrapAround) {
          // Wrap around to end
          prevIndex = (prevIndex + screens.length) % screens.length;
        } else if (prevIndex < 0) {
          // Don't navigate if at beginning
          setSwipeState(null);
          return;
        }

        navigate(`${basePath}/${screens[prevIndex]}`);
      }
    }

    setSwipeState(null);
  };

  return {
    swipeRef,
    handleTouchStart,
    handleTouchEnd,
  };
}
